import { Controller } from "@hotwired/stimulus"

/**
 * Report Form Controller
 * Tracks form changes and provides state for tab navigation
 */
export default class extends Controller {
  static values = {
    originalData: String,
    changed: Boolean
  }

  static targets = ["form"]

  connect() {
    console.log("Report form controller connected")
    this.changedValue = false

    // Delay storing original form data to avoid false positives from dynamic content
    setTimeout(() => {
      this.originalDataValue = this.serializeForm()
      console.log('[report-form] Original form data stored after page load')
    }, 500)

    // Listen for form input changes
    this.formTarget.addEventListener('input', this.handleInput.bind(this))
    this.formTarget.addEventListener('change', this.handleChange.bind(this))

    // Listen for save completion from old jQuery code
    this.formTarget.addEventListener('report:saved', this.handleReportSaved.bind(this))
  }

  disconnect() {
    this.formTarget.removeEventListener('input', this.handleInput.bind(this))
    this.formTarget.removeEventListener('change', this.handleChange.bind(this))
    this.formTarget.removeEventListener('report:saved', this.handleReportSaved.bind(this))
  }

  handleInput(event) {
    this.checkForChanges()
  }

  handleChange(event) {
    this.checkForChanges()
  }

  checkForChanges() {
    // Don't check for changes until original data is captured
    if (!this.originalDataValue) {
      console.log('[report-form] Skipping change check - original data not yet captured')
      return
    }

    const currentData = this.serializeForm()
    const hasChanges = currentData !== this.originalDataValue

    if (hasChanges !== this.changedValue) {
      this.changedValue = hasChanges
      console.log('[report-form] Form changed status:', hasChanges)
      // Dispatch custom event for other controllers to listen
      this.dispatch('changed', { detail: { hasChanges } })
    }
  }

  handleReportSaved(event) {
    console.log('[report-form] Received report:saved event', event.detail)
    this.markAsSaved()
  }

  serializeForm() {
    // Use FormData to serialize the form including files
    const formData = new FormData(this.formTarget)
    const serialized = new URLSearchParams()
    const skippedKeys = []

    // Convert FormData to URLSearchParams for comparison
    for (const [key, value] of formData.entries()) {
      // Skip file inputs in comparison (they're handled separately)
      if (value instanceof File) {
        skippedKeys.push(key)
        continue
      }

      // Skip empty trix-editor inputs that haven't been touched
      if (key === 'report[comment]' && value === '') {
        continue
      }

      serialized.append(key, value)
    }

    if (skippedKeys.length > 0) {
      console.log('[report-form] Skipped file fields:', skippedKeys)
    }

    return serialized.toString()
  }

  hasUnsavedChanges() {
    // Don't report changes until original data is captured
    if (!this.originalDataValue) {
      console.log('[report-form] hasUnsavedChanges: false (original data not yet captured)')
      return false
    }
    console.log('[report-form] hasUnsavedChanges:', this.changedValue)
    return this.changedValue
  }

  resetChangeTracking() {
    this.originalDataValue = this.serializeForm()
    this.changedValue = false
    this.dispatch('reset', { detail: { hasChanges: false } })
  }

  // Called by tab navigation controller after successful save
  markAsSaved() {
    this.resetChangeTracking()
  }

  /**
   * Save the form via AJAX and return a Promise
   * This replaces the old jQuery-based save mechanism
   * @returns {Promise} Resolves with response data, rejects with error
   */
  async saveFormData() {
    console.log('[report-form] Starting form save via Promise-based method')

    const formData = new FormData(this.formTarget)

    // Get task_id and report_id from hidden fields outside the form
    const taskId = document.getElementById('task_id')?.value || $('#task_id').val()
    const reportId = document.getElementById('report_id')?.value || $('#report_id').val()

    console.log('[report-form] Found IDs:', { taskId, reportId })

    if (!taskId || !reportId) {
      console.error('[report-form] Missing task_id or report_id')
      throw new Error('Missing required parameters: task_id or report_id not found')
    }

    const url = `/tasks/${taskId}/reports/${reportId}`

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      const data = await response.json()

      // Check if the server returned an error status
      if (!response.ok || data.status === 'error') {
        const errorMsg = data.errors?.join(', ') || data.message || `HTTP ${response.status}`
        console.error('[report-form] Server returned error:', errorMsg)
        throw new Error(errorMsg)
      }

      console.log('[report-form] Save successful', data)

      // Mark as saved and dispatch event
      this.markAsSaved()
      this.dispatch('saved', { detail: { data } })

      // Also dispatch the old event for backward compatibility
      const legacyEvent = new CustomEvent('report:saved', {
        bubbles: true,
        detail: {
          reportId: data.report?.id || reportId,
          timestamp: new Date().toISOString()
        }
      })
      this.formTarget.dispatchEvent(legacyEvent)

      return data
    } catch (error) {
      console.error('[report-form] Save failed', error)
      this.dispatch('save-error', { detail: { error: error.message } })
      throw error
    }
  }

  /**
   * Handle save button click in tabs - saves and navigates to next tab
   * Called when user clicks "Save Changes" button at bottom of Edit or Equipment tabs
   */
  async saveForm(event) {
    event.preventDefault()
    console.log('[report-form] Save button clicked, saving and navigating to next tab')

    // Check if there are any changes to save
    if (!this.hasUnsavedChanges()) {
      console.log('[report-form] No changes detected, skipping save and navigating to next tab')
      this.navigateToNextTab()
      return
    }

    // Disable the button to prevent double-clicks
    const button = event.currentTarget
    const originalHTML = button.innerHTML
    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Saving...'

    try {
      // Save the form using Promise-based method
      await this.saveFormData()

      console.log('[report-form] Save successful, navigating to next tab')

      // Navigate to next tab
      this.navigateToNextTab()
    } catch (error) {
      console.error('[report-form] Save failed:', error)

      // Re-enable button
      button.disabled = false
      button.innerHTML = originalHTML

      // Show error message
      alert(`Failed to save: ${error.message}. Please try again.`)
    }
  }

  navigateToNextTab() {
    console.log('[report-form] Navigating to next tab')

    // Find the tab navigation controller
    const tabNavController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller~="tab-navigation"]'),
      'tab-navigation'
    )

    if (!tabNavController) {
      console.error('[report-form] Tab navigation controller not found')
      return
    }

    // Get current fieldset to determine which tab we're on
    const currentFieldset = tabNavController.fieldsetTargets.find(fs => $(fs).is(':visible'))
    if (!currentFieldset) {
      console.error('[report-form] No visible fieldset found')
      return
    }

    const currentFieldsetId = currentFieldset.id
    console.log('[report-form] Current fieldset:', currentFieldsetId)

    // Map fieldset to next fieldset
    const nextFieldsetMap = {
      'fieldset_1': 'fieldset_2',  // Edit -> Equipment
      'fieldset_2': 'fieldset_3',  // Equipment -> Additional Pages
      'fieldset_3': 'fieldset_4',  // Additional Pages -> Signatures
      'fieldset_4': 'fieldset_5',  // Signatures -> Preview
      'fieldset_5': 'fieldset_5'   // Preview -> Stay on Preview
    }

    const nextFieldsetId = nextFieldsetMap[currentFieldsetId]
    console.log('[report-form] Next fieldset:', nextFieldsetId)

    if (nextFieldsetId) {
      // Find the tab that corresponds to this fieldset
      const nextTab = tabNavController.tabTargets.find(tab => {
        const fieldsetId = tabNavController.getFieldsetId(tab)
        return fieldsetId === nextFieldsetId
      })

      if (nextTab) {
        console.log('[report-form] Triggering click on next tab:', nextTab.id)
        nextTab.click()
      }
    }
  }
}
